import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Layout,
  Select,
  Upload,
  Typography,
  message,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { InboxOutlined } from "@ant-design/icons";
import { Get } from "../../config/api/get";
import { Post } from "../../config/api/post";
import { ASSETS, COURSE, CONTENT_TYPE } from "../../config/constants";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";

const { TextArea } = Input;

const ASSET_TYPES = [
  { value: "IMAGE", label: "IMAGE" },
  { value: "VIDEO", label: "VIDEO" },
  { value: "PDF", label: "PDF" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const ACCEPT_BY_TYPE = {
  IMAGE: "image/*",
  VIDEO: "video/*",
  PDF: ".pdf,application/pdf",
};

function AssetAdd() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedType, setSelectedType] = useState("IMAGE");

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourses = async () => {
    try {
      const res = await Get(COURSE.getAllCourses, token, { limit: "500" });
      const data = res?.data ?? res;
      const list = data?.docs ?? data ?? [];
      setCourses(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error("Failed to load courses");
    }
  };

  const beforeUpload = (file) => {
    const type = form.getFieldValue("type") || selectedType;
    const accept = ACCEPT_BY_TYPE[type] || "";
    const isImage = type === "IMAGE" && file.type.startsWith("image/");
    const isVideo = type === "VIDEO" && file.type.startsWith("video/");
    const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
    if (!isImage && !isVideo && !isPdf) {
      message.error(`File type must match asset type "${type}". Allowed: ${accept}`);
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    return false;
  };

  const onFinish = (values) => {
    const type = values.type || "IMAGE";
    const file = fileList[0];
    if (!file) {
      message.error("Please upload a file");
      return;
    }
    const isImage = type === "IMAGE" && file.type?.startsWith("image/");
    const isVideo = type === "VIDEO" && file.type?.startsWith("video/");
    const isPdf = type === "PDF" && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));
    if (!isImage && !isVideo && !isPdf) {
      message.error(`File type must match asset type "${type}"`);
      return;
    }

    const formData = new FormData();
    if (type === "PDF") {
      formData.append("files", file);
    } else if (type === "IMAGE") {
      formData.append("image", file);
    } else if (type === "VIDEO") {
      formData.append("video", file);
    }
    formData.append("title", values.title);
    formData.append("description", values.description || "");
    formData.append("type", type);
    formData.append("status", values.status || "ACTIVE");
    if (values.courses && values.courses.length) {
      values.courses.forEach((courseId) => formData.append("courses", courseId));
    }

    setLoading(true);
    Post(ASSETS.createAsset, formData, token, null, CONTENT_TYPE.FORM_DATA)
      .then((res) => {
        setLoading(false);
        const data = res?.data ?? res;
        if (data?.status) {
          swal("Success!", "Asset created successfully", "success");
          navigate("/asset-management");
        } else {
          swal("Error", data?.message || "Failed to create asset", "error");
        }
      })
      .catch(() => setLoading(false));
  };

  return (
    <Layout className="configuration">
      <div className="boxDetails2">
        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12} style={{ display: "flex", alignItems: "center" }}>
            <FaArrowLeft
              style={{ fontWeight: "bold", fontSize: "20px", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            &emsp;
            <h1 className="pageTitle" style={{ margin: 0 }}>
              Add Asset
            </h1>
          </Col>
        </Row>
        <br />

        <Form
          form={form}
          layout="vertical"
          name="assetForm"
          className="contactForm"
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}
          onFinish={onFinish}
          initialValues={{ status: "ACTIVE", type: "IMAGE" }}
          autoComplete="off"
        >
          <Row style={{ padding: "20px 48px 20px 48px" }}>
            <Col xs={24} md={18} offset={{ md: 3 }} style={{ maxWidth: 720 }}>
              <Row gutter={20}>
                <Col xs={24}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    Title
                  </Typography.Title>
                  <Form.Item
                    name="title"
                    rules={[{ required: true, message: "Please enter title" }]}
                    style={{ marginBottom: 20 }}
                  >
                    <Input
                      size="large"
                      placeholder="Asset title"
                      className="signupFormInput"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    Description
                  </Typography.Title>
                  <Form.Item name="description" style={{ marginBottom: 20 }}>
                    <TextArea
                      rows={4}
                      placeholder="Description"
                      className="signupFormInput"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    Type
                  </Typography.Title>
                  <Form.Item
                    name="type"
                    rules={[{ required: true, message: "Please select type" }]}
                    style={{ marginBottom: 20 }}
                  >
                    <Select
                      size="large"
                      placeholder="Select type"
                      className="signupSelectBox"
                      options={ASSET_TYPES}
                      onChange={(v) => {
                        setSelectedType(v);
                        setFileList([]);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    Status
                  </Typography.Title>
                  <Form.Item name="status" style={{ marginBottom: 20 }}>
                    <Select
                      size="large"
                      className="signupSelectBox"
                      options={STATUS_OPTIONS}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    File Upload
                  </Typography.Title>
                  <Form.Item
                    required
                    style={{ marginBottom: 20 }}
                    extra={`Allowed for ${selectedType}: ${ACCEPT_BY_TYPE[selectedType] || "—"}`}
                  >
                    <Upload.Dragger
                      maxCount={1}
                      fileList={fileList.map((f) => ({ uid: f.uid || "-", name: f.name, status: "done" }))}
                      beforeUpload={beforeUpload}
                      onRemove={() => setFileList([])}
                      accept={ACCEPT_BY_TYPE[selectedType]}
                      style={{ padding: "16px 0" }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ fontSize: 48, color: "#2484c6" }} />
                      </p>
                      <p className="ant-upload-text">Click or drag file to this area</p>
                    </Upload.Dragger>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Typography.Title
                    className="fontFamily1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "black",
                      textAlign: "left",
                      marginBottom: 8,
                    }}
                  >
                    Courses
                  </Typography.Title>
                  <Form.Item name="courses" style={{ marginBottom: 20 }}>
                    <Select
                      mode="multiple"
                      size="large"
                      placeholder="Select courses"
                      className="signupSelectBox courseSelectBox2"
                      optionFilterProp="label"
                      options={courses.map((c) => ({ value: c._id, label: c.title || c.name }))}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Row style={{ marginTop: 30 }} gutter={12}>
                    <Col>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="loginButton"
                        loading={loading}
                      >
                        {loading ? "Saving..." : "Save"}
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        className="fontFamily1"
                        style={{
                          padding: "10px 30px",
                          cursor: "pointer",
                          color: "black",
                          borderRadius: "5px",
                          height: "auto",
                          border: "1px solid #203657",
                          fontWeight: "bold",
                        }}
                        ghost
                        size="large"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      <br />
      <br />
    </Layout>
  );
}

export default AssetAdd;
